import React, { useState, useEffect, useCallback } from 'react';
import { getClasses, saveClasses } from '../utils/api';
import { useWindowSize } from '../utils/useWindowSize';

// Field names as stored in ClassScheduleSchema.
// UI labels differ for 3 fields: nextDate→"Date", time→"Start Time", maxSeats→"Max Students"
const EMPTY_CLASS = {
  courseName:       '',
  courseCode:       '',
  description:      '',
  nextDate:         '',
  endDate:          '',
  time:             '',
  endTime:          '',
  venue:            '',
  fee:              '',
  maxSeats:         '',
  prerequisites:    '',
  registrationLink: ''
};

const REQUIRED = ['courseName', 'nextDate', 'fee', 'venue'];

function formatDate(isoStr) {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  return isNaN(d.getTime()) ? isoStr : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function ClassManager() {
  const { isMobile } = useWindowSize();

  const [classes,        setClasses]        = useState([]);
  const [skillActivated, setSkillActivated] = useState({});
  const [loading,        setLoading]        = useState(true);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState('');
  const [banner,         setBanner]         = useState('');    // success/info message
  const [modal,          setModal]          = useState(null);  // null | { mode:'add'|'edit', index:number|null, form:{} }
  const [deleteConfirm,  setDeleteConfirm]  = useState(null);  // index to delete, or null
  const [formErrors,     setFormErrors]     = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getClasses();
      setClasses(res.data.data.classSchedule || []);
      setSkillActivated(res.data.data.skillActivated || {});
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load class schedule');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openAdd() {
    setFormErrors([]);
    setModal({ mode: 'add', index: null, form: { ...EMPTY_CLASS } });
  }

  function openEdit(index) {
    setFormErrors([]);
    const cls = classes[index];
    setModal({
      mode: 'edit',
      index,
      form: {
        courseName:       cls.courseName       || '',
        courseCode:       cls.courseCode       || '',
        description:      cls.description      || '',
        nextDate:         cls.nextDate ? cls.nextDate.split('T')[0] : '',
        endDate:          cls.endDate  ? cls.endDate.split('T')[0]  : '',
        time:             cls.time             || '',
        endTime:          cls.endTime          || '',
        venue:            cls.venue            || '',
        fee:              cls.fee              != null ? String(cls.fee) : '',
        maxSeats:         cls.maxSeats         != null ? String(cls.maxSeats) : '',
        prerequisites:    cls.prerequisites    || '',
        registrationLink: cls.registrationLink || ''
      }
    });
  }

  function handleFormChange(field, value) {
    setModal(m => ({ ...m, form: { ...m.form, [field]: value } }));
    setFormErrors(prev => prev.filter(e => !e.includes(`'${field}'`)));
  }

  function validateForm(form) {
    const errors = [];
    for (const f of REQUIRED) {
      if (!form[f] || String(form[f]).trim() === '') {
        const label = f === 'nextDate' ? 'date' : f;
        errors.push(`'${label}' is required`);
      }
    }
    if (form.fee !== '' && (isNaN(Number(form.fee)) || Number(form.fee) < 0)) {
      errors.push("'fee' must be a non-negative number");
    }
    return errors;
  }

  async function handleSave() {
    const errors = validateForm(modal.form);
    if (errors.length > 0) { setFormErrors(errors); return; }

    const updated = [...classes];
    const entry = {
      ...modal.form,
      fee:      modal.form.fee      !== '' ? Number(modal.form.fee)      : undefined,
      maxSeats: modal.form.maxSeats !== '' ? Number(modal.form.maxSeats) : undefined
    };

    if (modal.mode === 'add') {
      updated.push(entry);
    } else {
      updated[modal.index] = entry;
    }

    await persist(updated);
    setModal(null);
  }

  async function handleDelete() {
    const updated = classes.filter((_, i) => i !== deleteConfirm);
    setDeleteConfirm(null);
    await persist(updated);
  }

  async function persist(newSchedule) {
    setSaving(true);
    setError('');
    setBanner('');
    try {
      const res = await saveClasses(newSchedule);
      const saved    = res.data.data.classSchedule;
      const newSkill = res.data.data.skillActivated;
      const justActivated = !skillActivated.classBooking && newSkill.classBooking;
      setClasses(saved);
      setSkillActivated(newSkill);
      if (justActivated) {
        setBanner('active');
      } else {
        setBanner('saved');
        setTimeout(() => setBanner(''), 3000);
      }
    } catch (err) {
      const detail = err?.response?.data?.errors || [];
      setError(detail.length > 0 ? detail.join(', ') : (err?.response?.data?.message || 'Failed to save'));
    } finally {
      setSaving(false);
    }
  }

  const s = styles(isMobile);

  if (loading) return <div style={s.empty}>Loading class schedule…</div>;

  return (
    <div style={s.wrap}>

      {/* Page header */}
      <div style={s.header}>
        <div>
          <div style={s.pageTitle}>Classes &amp; Courses</div>
          <div style={s.pageSubtitle}>Manage your centre's class schedule. The class enquiry agent reads from this list.</div>
        </div>
        <button onClick={openAdd} style={s.addBtn} disabled={saving}>
          + Add Class
        </button>
      </div>

      {/* Agent active banner */}
      {banner === 'active' && (
        <div style={s.activeBanner}>
          <span style={s.activeDot} />
          Class enquiry agent is now ACTIVE — your AI agent can now answer class schedule questions.
        </div>
      )}

      {/* Saved confirmation */}
      {banner === 'saved' && (
        <div style={s.savedBanner}>Class schedule saved.</div>
      )}

      {/* Error */}
      {error && <div style={s.errorBanner}>{error}</div>}

      {/* Skill status badge */}
      {!loading && (
        <div style={s.statusRow}>
          <span style={skillActivated.classBooking ? s.statusActive : s.statusInactive}>
            {skillActivated.classBooking ? '● Class enquiry agent: ACTIVE' : '○ Class enquiry agent: inactive (add a class to activate)'}
          </span>
        </div>
      )}

      {/* Empty state */}
      {classes.length === 0 ? (
        <div style={s.emptyState}>
          <div style={s.emptyIcon}>📅</div>
          <div style={s.emptyTitle}>No classes yet</div>
          <div style={s.emptyText}>Add your first class to activate the class enquiry agent.</div>
          <button onClick={openAdd} style={s.addBtn}>+ Add Class</button>
        </div>
      ) : (
        /* Class list */
        <div style={s.list}>
          {classes.map((cls, i) => (
            <div key={i} style={s.card}>
              <div style={s.cardTop}>
                <div style={s.cardLeft}>
                  {cls.courseCode && <span style={s.codeChip}>{cls.courseCode}</span>}
                  <div style={s.courseName}>{cls.courseName}</div>
                  <div style={s.cardMeta}>
                    {formatDate(cls.nextDate)}
                    {cls.endDate && ` – ${formatDate(cls.endDate)}`}
                    {(cls.time || cls.endTime) && ` · ${cls.time || ''}${cls.endTime ? ` – ${cls.endTime}` : ''}`}
                  </div>
                </div>
                <div style={s.cardRight}>
                  <div style={s.fee}>₹{Number(cls.fee).toLocaleString('en-IN')}</div>
                  <div style={s.cardActions}>
                    <button onClick={() => openEdit(i)} style={s.editBtn}>Edit</button>
                    <button onClick={() => setDeleteConfirm(i)} style={s.deleteBtn}>Delete</button>
                  </div>
                </div>
              </div>
              {cls.venue && <div style={s.venue}>{cls.venue}</div>}
              {cls.description && <div style={s.desc}>{cls.description}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit modal */}
      {modal && (
        <div style={s.overlay} onClick={() => setModal(null)}>
          <div style={s.modalBox} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>{modal.mode === 'add' ? 'Add Class' : 'Edit Class'}</div>
              <button onClick={() => setModal(null)} style={s.closeBtn}>✕</button>
            </div>

            {formErrors.length > 0 && (
              <div style={s.formErrors}>
                {formErrors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}

            <div style={s.modalBody}>
              <div style={isMobile ? s.fieldsFull : s.fieldsGrid}>

                <div style={s.fieldWrap}>
                  <label style={s.label}>Course Name *</label>
                  <input style={s.input} value={modal.form.courseName}
                    onChange={e => handleFormChange('courseName', e.target.value)}
                    placeholder="e.g. GMCKS Basic Pranic Healing Workshop" />
                </div>

                <div style={s.fieldWrap}>
                  <label style={s.label}>Course Code</label>
                  <input style={s.input} value={modal.form.courseCode}
                    onChange={e => handleFormChange('courseCode', e.target.value)}
                    placeholder="e.g. BASIC-PH" />
                </div>

                <div style={s.fieldWrap}>
                  <label style={s.label}>Date *</label>
                  <input style={s.input} type="date" value={modal.form.nextDate}
                    onChange={e => handleFormChange('nextDate', e.target.value)} />
                </div>

                <div style={s.fieldWrap}>
                  <label style={s.label}>End Date</label>
                  <input style={s.input} type="date" value={modal.form.endDate}
                    onChange={e => handleFormChange('endDate', e.target.value)} />
                </div>

                <div style={s.fieldWrap}>
                  <label style={s.label}>Start Time</label>
                  <input style={s.input} type="time" value={modal.form.time}
                    onChange={e => handleFormChange('time', e.target.value)} />
                </div>

                <div style={s.fieldWrap}>
                  <label style={s.label}>End Time</label>
                  <input style={s.input} type="time" value={modal.form.endTime}
                    onChange={e => handleFormChange('endTime', e.target.value)} />
                </div>

                <div style={s.fieldWrap}>
                  <label style={s.label}>Venue *</label>
                  <input style={s.input} value={modal.form.venue}
                    onChange={e => handleFormChange('venue', e.target.value)}
                    placeholder="e.g. Pranic Wellness Centre, Perungudi" />
                </div>

                <div style={s.fieldWrap}>
                  <label style={s.label}>Fee (₹) *</label>
                  <input style={s.input} type="number" min="0" value={modal.form.fee}
                    onChange={e => handleFormChange('fee', e.target.value)}
                    placeholder="e.g. 5500" />
                </div>

                <div style={s.fieldWrap}>
                  <label style={s.label}>Max Students</label>
                  <input style={s.input} type="number" min="1" value={modal.form.maxSeats}
                    onChange={e => handleFormChange('maxSeats', e.target.value)}
                    placeholder="e.g. 30" />
                </div>

                <div style={s.fieldWrap}>
                  <label style={s.label}>Prerequisites</label>
                  <input style={s.input} value={modal.form.prerequisites}
                    onChange={e => handleFormChange('prerequisites', e.target.value)}
                    placeholder="e.g. GMCKS Basic Pranic Healing" />
                </div>

                <div style={{ ...s.fieldWrap, gridColumn: isMobile ? undefined : 'span 2' }}>
                  <label style={s.label}>Registration Link</label>
                  <input style={s.input} value={modal.form.registrationLink}
                    onChange={e => handleFormChange('registrationLink', e.target.value)}
                    placeholder="e.g. https://example.com/register" />
                </div>

                <div style={{ ...s.fieldWrap, gridColumn: isMobile ? undefined : 'span 2' }}>
                  <label style={s.label}>Description</label>
                  <textarea style={s.textarea} rows={3} value={modal.form.description}
                    onChange={e => handleFormChange('description', e.target.value)}
                    placeholder="Short course description shown to patients…" />
                </div>

              </div>
            </div>

            <div style={s.modalFooter}>
              <button onClick={() => setModal(null)} style={s.cancelBtn}>Cancel</button>
              <button onClick={handleSave} style={s.saveBtn} disabled={saving}>
                {saving ? 'Saving…' : (modal.mode === 'add' ? 'Add Class' : 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteConfirm !== null && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={s.confirmBox} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Delete class?</div>
            <div style={s.confirmText}>
              "{classes[deleteConfirm]?.courseName}" will be removed from the schedule.
              {classes.length === 1 && ' This is the last class — the class enquiry agent will become inactive.'}
            </div>
            <div style={s.modalFooter}>
              <button onClick={() => setDeleteConfirm(null)} style={s.cancelBtn}>Cancel</button>
              <button onClick={handleDelete} style={s.deleteConfirmBtn} disabled={saving}>
                {saving ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

function styles(isMobile) {
  return {
    wrap:        { padding: isMobile ? '16px' : '24px', fontFamily: "'Open Sans', sans-serif" },
    header:      { display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: '16px', marginBottom: '20px' },
    pageTitle:   { fontFamily: "'Raleway', sans-serif", fontSize: isMobile ? '18px' : '22px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' },
    pageSubtitle:{ fontSize: '13px', color: '#666' },
    addBtn:      { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '6px', padding: '12px 20px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap', minHeight: '44px' },

    activeBanner:{ display: 'flex', alignItems: 'center', gap: '10px', background: '#e8f5e9', border: '1px solid #4caf50', borderRadius: '8px', padding: '14px 18px', marginBottom: '16px', color: '#2e7d32', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700' },
    activeDot:   { width: '10px', height: '10px', borderRadius: '50%', background: '#4caf50', flexShrink: 0 },
    savedBanner: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', color: '#166534', fontSize: '13px' },
    errorBanner: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', color: '#dc2626', fontSize: '13px' },

    statusRow:   { marginBottom: '20px' },
    statusActive:{ fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#4caf50', letterSpacing: '0.5px' },
    statusInactive:{ fontFamily: "'Raleway', sans-serif", fontSize: '12px', color: '#999', letterSpacing: '0.5px' },

    empty:       { padding: '40px', textAlign: 'center', color: '#999' },
    emptyState:  { textAlign: 'center', padding: '60px 20px', background: '#f9faf7', borderRadius: '12px', border: '2px dashed #e0e0e0' },
    emptyIcon:   { fontSize: '40px', marginBottom: '12px' },
    emptyTitle:  { fontFamily: "'Raleway', sans-serif", fontSize: '18px', fontWeight: '700', color: '#1a1a2e', marginBottom: '8px' },
    emptyText:   { fontSize: '14px', color: '#666', marginBottom: '24px' },

    list:        { display: 'flex', flexDirection: 'column', gap: '12px' },
    card:        { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: isMobile ? '16px' : '20px' },
    cardTop:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: isMobile ? 'wrap' : 'nowrap' },
    cardLeft:    { flex: 1, minWidth: 0 },
    cardRight:   { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 },
    codeChip:    { display: 'inline-block', background: '#e8f5f2', color: '#4a8f82', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '6px' },
    courseName:  { fontFamily: "'Raleway', sans-serif", fontSize: isMobile ? '14px' : '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' },
    cardMeta:    { fontSize: '12px', color: '#888' },
    fee:         { fontFamily: "'Raleway', sans-serif", fontSize: '16px', fontWeight: '700', color: '#4a8f82' },
    cardActions: { display: 'flex', gap: '8px' },
    venue:       { fontSize: '12px', color: '#666', marginTop: '8px' },
    desc:        { fontSize: '12px', color: '#888', marginTop: '6px', lineHeight: '1.5' },

    editBtn:     { background: 'transparent', border: '1px solid #4a8f82', color: '#4a8f82', borderRadius: '4px', padding: '6px 14px', fontSize: '11px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", cursor: 'pointer', minHeight: '44px', minWidth: '44px' },
    deleteBtn:   { background: 'transparent', border: '1px solid #e74c3c', color: '#e74c3c', borderRadius: '4px', padding: '6px 14px', fontSize: '11px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", cursor: 'pointer', minHeight: '44px', minWidth: '44px' },

    overlay:     { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '20px' },
    modalBox:    { background: '#fff', borderRadius: isMobile ? '16px 16px 0 0' : '12px', width: '100%', maxWidth: isMobile ? '100%' : '680px', maxHeight: isMobile ? '92vh' : '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    confirmBox:  { background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '420px', padding: '28px', display: 'flex', flexDirection: 'column', gap: '16px' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #e0e0e0', flexShrink: 0 },
    modalTitle:  { fontFamily: "'Raleway', sans-serif", fontSize: '16px', fontWeight: '700', color: '#1a1a2e' },
    closeBtn:    { background: 'none', border: 'none', fontSize: '18px', color: '#999', cursor: 'pointer', padding: '4px', minHeight: '44px', minWidth: '44px' },
    modalBody:   { padding: '20px 24px', overflowY: 'auto', flex: 1 },
    modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', borderTop: '1px solid #e0e0e0', flexShrink: 0 },

    fieldsGrid:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
    fieldsFull:  { display: 'flex', flexDirection: 'column', gap: '16px' },
    fieldWrap:   {},
    label:       { display: 'block', fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666', marginBottom: '6px', fontFamily: "'Raleway', sans-serif" },
    input:       { width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '16px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', color: '#333', outline: 'none' },
    textarea:    { width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '16px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', color: '#333', resize: 'vertical', outline: 'none' },

    formErrors:  { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '12px 16px', margin: '0 24px', color: '#dc2626', fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '4px' },

    cancelBtn:   { background: 'transparent', border: '1px solid #e0e0e0', color: '#666', borderRadius: '6px', padding: '10px 20px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' },
    saveBtn:     { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '6px', padding: '10px 24px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', minHeight: '44px' },
    deleteConfirmBtn: { background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '6px', padding: '10px 24px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' },
    confirmText: { fontSize: '14px', color: '#444', lineHeight: '1.6' },
  };
}
